// components/ValuationPanel.jsx
import React, { useMemo, useEffect, useState } from "react";

/** ----------- utilities ----------- */
const num = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const raw = typeof v === "object" && v !== null ? (v.value ?? "") : String(v);
    const n = Number(String(raw).replace(/[,\s₹%]/g, ""));
    return Number.isFinite(n) ? n : null;
};

function latestFrom(financials, section, labelRegex) {
    const sec = financials?.[section];
    if (!sec) return null;
    let best = null;
    for (const [label, yearly] of Object.entries(sec)) {
        if (!labelRegex.test(label.toLowerCase())) continue;
        const years = Object.keys(yearly).sort((a, b) => Number(a) - Number(b));
        for (let i = years.length - 1; i >= 0; i--) {
            const v = num(yearly[years[i]]);
            if (v != null) {
                best = v;
                break;
            }
        }
    }
    return best;
}
const coalesce = (...xs) => xs.find((x) => x != null) ?? null;

const DEFAULT_DISCOUNTS = {
    cash: 0,         // keep 100%
    receivables: 15, // keep 85%
    inventory: 40,   // keep 60%
    ppe: 75,         // keep 25%
    residual: 50     // keep 50% of "everything else"
};

const LS_KEY_DISC = "finlytics.valuation.discounts";
const LS_KEY_MODE = "finlytics.valuation.autoMode";
const LS_KEY_SECTOR = "finlytics.valuation.sector";

/**
 * Sector/industry base presets (Buffett-style conservatism)
 * Lower = better quality/liquidity, Higher = riskier buckets.
 */
const SECTOR_BASE = {
    "FMCG": { cash: 0, receivables: 10, inventory: 15, ppe: 60, residual: 40 },
    "IT Services / SaaS": { cash: 0, receivables: 12, inventory: 0, ppe: 70, residual: 40 },
    "Retail": { cash: 0, receivables: 15, inventory: 25, ppe: 70, residual: 45 },
    "Auto/Auto Ancillary": { cash: 0, receivables: 20, inventory: 30, ppe: 70, residual: 50 },
    "Chemicals": { cash: 0, receivables: 22, inventory: 35, ppe: 75, residual: 55 },
    "Capital Goods": { cash: 0, receivables: 25, inventory: 35, ppe: 75, residual: 55 },
    "Trading/Distribution": { cash: 0, receivables: 25, inventory: 35, ppe: 80, residual: 55 },
    "Metals/Commodities": { cash: 0, receivables: 25, inventory: 40, ppe: 80, residual: 60 },
    "Pharma": { cash: 0, receivables: 15, inventory: 25, ppe: 70, residual: 45 },
    "Other": { ...DEFAULT_DISCOUNTS }
};

/** Quantitative override rules (hard, conservative) */
function computeAutoDiscounts({
    sector,
    debtorDays,
    inventoryDays,
    cfo,
    pat,
    base = SECTOR_BASE["Other"]
}) {
    // start from sector base
    const disc = { ...base };
    const notes = [];

    // 1) Receivables haircuts by Debtor Days (your rule)
    if (debtorDays != null) {
        if (debtorDays > 150) {
            disc.receivables = Math.max(disc.receivables, 40);
            notes.push(`Debtor Days ${debtorDays}d >150 ⇒ AR haircut 40%`);
        } else if (debtorDays > 120) {
            disc.receivables = Math.max(disc.receivables, 35);
            notes.push(`Debtor Days ${debtorDays}d >120 ⇒ AR haircut 35%`);
        } else if (debtorDays > 90) {
            disc.receivables = Math.max(disc.receivables, 30);
            notes.push(`Debtor Days ${debtorDays}d >90 ⇒ AR haircut 30%`);
        } else if (debtorDays > 60) {
            disc.receivables = Math.max(disc.receivables, 20);
            notes.push(`Debtor Days ${debtorDays}d >60 ⇒ AR haircut 20%`);
        }
    }

    // 2) Inventory haircuts by Inventory Days (your rule)
    if (inventoryDays != null) {
        if (inventoryDays > 180) {
            disc.inventory = Math.max(disc.inventory, 45);
            notes.push(`Inventory Days ${inventoryDays}d >180 ⇒ Inv haircut 45%`);
        } else if (inventoryDays > 120) {
            disc.inventory = Math.max(disc.inventory, 40);
            notes.push(`Inventory Days ${inventoryDays}d >120 ⇒ Inv haircut 40%`);
        } else if (inventoryDays > 90) {
            disc.inventory = Math.max(disc.inventory, 30);
            notes.push(`Inventory Days ${inventoryDays}d >90 ⇒ Inv haircut 30%`);
        } else if (inventoryDays > 60) {
            disc.inventory = Math.max(disc.inventory, 20);
            notes.push(`Inventory Days ${inventoryDays}d >60 ⇒ Inv haircut 20%`);
        }
    }

    // 3) Profit quality test — CFO vs PAT (Owner-earnings lens)
    if (cfo != null && pat != null) {
        if (cfo < 0 && pat > 0) {
            // very weak cash conversion → mark down WC + residual strongly
            disc.receivables = Math.max(disc.receivables, 40);
            disc.inventory = Math.max(disc.inventory, 40);
            disc.residual = Math.max(disc.residual, 60);
            notes.push(`CFO (${cfo}) < 0 while PAT (${pat}) > 0 ⇒ severe WC/residual haircuts`);
        } else if (cfo < 0.5 * pat) {
            disc.receivables = Math.max(disc.receivables, 35);
            disc.inventory = Math.max(disc.inventory, 35);
            notes.push(`CFO (${cfo}) < 0.5×PAT (${pat}) ⇒ raise AR/Inv haircuts`);
        }
    }

    // 4) Sector specific nudge (trading/commodities heavy PPE risk)
    if (sector && /trading|commodity|metals|capital goods/i.test(sector)) {
        disc.ppe = Math.max(disc.ppe, 80);
        notes.push(`Sector ${sector} ⇒ PPE haircut ≥80%`);
    }

    // bounds
    for (const k of Object.keys(disc)) {
        disc[k] = Math.max(0, Math.min(100, Math.round(disc[k])));
    }

    return { disc, notes };
}

const fmt = (x, d = 2) =>
    x == null || !Number.isFinite(x)
        ? "–"
        : x.toLocaleString(undefined, { maximumFractionDigits: d });

/** ----------- component ----------- */
export default function ValuationPanel({
    financials,
    currentPrice,
    sharesOutstanding,
    sector: sectorProp,     // optional prop
    industry: industryProp  // optional alternative
}) {
    /** --- UI state --- */
    const [disc, setDisc] = useState(DEFAULT_DISCOUNTS);
    const [autoMode, setAutoMode] = useState(true);
    const [sector, setSector] = useState("Other");
    const [whyNotes, setWhyNotes] = useState([]);

    /** Load persisted settings */
    useEffect(() => {
        try {
            const rawD = localStorage.getItem(LS_KEY_DISC);
            const rawM = localStorage.getItem(LS_KEY_MODE);
            const rawS = localStorage.getItem(LS_KEY_SECTOR);
            if (rawD) setDisc({ ...DEFAULT_DISCOUNTS, ...JSON.parse(rawD) });
            if (rawM) setAutoMode(JSON.parse(rawM));
            if (rawS) setSector(rawS);
        } catch { }
    }, []);

    /** Persist settings */
    useEffect(() => {
        try { localStorage.setItem(LS_KEY_DISC, JSON.stringify(disc)); } catch { }
    }, [disc]);
    useEffect(() => {
        try { localStorage.setItem(LS_KEY_MODE, JSON.stringify(autoMode)); } catch { }
    }, [autoMode]);
    useEffect(() => {
        try { localStorage.setItem(LS_KEY_SECTOR, sector); } catch { }
    }, [sector]);

    /** Try to infer sector from props if provided */
    useEffect(() => {
        const fromProps = industryProp || sectorProp;
        if (fromProps && fromProps.trim()) {
            // map to one of our buckets if possible
            const map = Object.keys(SECTOR_BASE);
            const hit = map.find(k => fromProps.toLowerCase().includes(k.toLowerCase()));
            setSector(hit || "Other");
        }
    }, [sectorProp, industryProp]);

    /** -------- Balance Sheet / Ratios pulls -------- */
    const totalAssets = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /total\s*assets/),
                latestFrom(financials, "balanceSheet", /^assets$/)
            ),
        [financials]
    );
    const totalLiabilities = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /total\s*liab/),
                latestFrom(financials, "balanceSheet", /^liabilities$/)
            ),
        [financials]
    );
    const cash = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /(cash|bank|cash\s*and\s*equivalents)/),
                0
            ),
        [financials]
    );
    const receivables = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /(accounts|trade)\s*receiv/),
                latestFrom(financials, "balanceSheet", /debtors/)
            ),
        [financials]
    );
    const inventory = useMemo(
        () => latestFrom(financials, "balanceSheet", /inventor/),
        [financials]
    );
    const currentAssets = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /total\s*current\s*assets/),
                (cash ?? 0) + (receivables ?? 0) + (inventory ?? 0)
            ),
        [financials, cash, receivables, inventory]
    );
    const intangible = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /(intangibles?|goodwill)/),
                0
            ),
        [financials]
    );
    const ppe = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "balanceSheet", /(property|plant|equipment|ppe)/),
                0
            ),
        [financials]
    );

    // Ratios for automation
    const debtorDays = useMemo(
        () => latestFrom(financials, "ratios", /(debtor|receivable)\s*days/),
        [financials]
    );
    const inventoryDays = useMemo(
        () => latestFrom(financials, "ratios", /inventory\s*days/),
        [financials]
    );

    // PAT & CFO for quality test
    const pat = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "pnl", /(net\s*profit|profit\s*after\s*tax)/),
                latestFrom(financials, "profitAndLoss", /(net\s*profit|profit\s*after\s*tax)/)
            ),
        [financials]
    );
    const cfo = useMemo(
        () =>
            coalesce(
                latestFrom(financials, "cashFlow", /(cash\s*from\s*operating|net\s*cash\s*from\s*operating)/),
                latestFrom(financials, "cashFlow", /operating\s*cash\s*flow/)
            ),
        [financials]
    );

    // ---------- Core valuations ----------
    const equityBook =
        totalAssets != null && totalLiabilities != null
            ? totalAssets - totalLiabilities
            : null;

    const tangibleBook =
        equityBook != null ? equityBook - (intangible ?? 0) : null;

    // --- AUTOMATION: recompute discounts in Auto mode ---
    useEffect(() => {
        if (!autoMode) return;
        const base = SECTOR_BASE[sector] || SECTOR_BASE["Other"];
        const { disc: autoDisc, notes } = computeAutoDiscounts({
            sector,
            debtorDays,
            inventoryDays,
            cfo,
            pat,
            base
        });
        setDisc(autoDisc);
        setWhyNotes(notes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoMode, sector, debtorDays, inventoryDays, cfo, pat]);

    // Adjustable Buffett-style value using sliders (discounts are % to subtract)
    const adjusted = useMemo(() => {
        if (equityBook == null) return null;
        const c = cash ?? 0;
        const ar = receivables ?? 0;
        const inv = inventory ?? 0;
        const fixed = ppe ?? 0;
        const known = c + ar + inv + fixed;
        const residual = Math.max(equityBook - known, 0);

        const keep = (amt, discountPct) => amt * (1 - (discountPct ?? 0) / 100);

        return (
            keep(c, disc.cash) +
            keep(ar, disc.receivables) +
            keep(inv, disc.inventory) +
            keep(fixed, disc.ppe) +
            keep(residual, disc.residual)
        );
    }, [equityBook, cash, receivables, inventory, ppe, disc]);

    const ncav =
        currentAssets != null && totalLiabilities != null
            ? currentAssets - totalLiabilities
            : null;
    const twoThirdsNcav = ncav != null ? (2 / 3) * ncav : null;

    // ---------- Per-share & multiples ----------
    const so = Number(sharesOutstanding) || null;
    const bvps = equityBook != null && so ? equityBook / so : null;
    const tbvps = tangibleBook != null && so ? tangibleBook / so : null;
    const adjps = adjusted != null && so ? adjusted / so : null;
    const ncavps = ncav != null && so ? ncav / so : null;
    const t2nps = twoThirdsNcav != null && so ? twoThirdsNcav / so : null;

    const price = Number(currentPrice) || null;
    const pb = price && bvps ? price / bvps : null;
    const ptbv = price && tbvps ? price / tbvps : null;
    const padj = price && adjps ? price / adjps : null;
    const pncav = price && ncavps ? price / ncavps : null;

    const mos = (val) => (price && val ? (val - price) / val : null);
    const chip = (ok) => (ok == null ? "chip" : ok ? "chip green" : "chip red");

    const handlePct = (k, v) =>
        setDisc((s) => ({ ...s, [k]: Math.max(0, Math.min(100, Number(v))) }));

    const reset = () => setDisc(DEFAULT_DISCOUNTS);

    // small helper for disabling sliders in auto mode
    const dis = autoMode ? { opacity: 0.6, pointerEvents: "none" } : {};

    return (
        <div className="rf-card">
            <h3 className="rf-title">Valuation (BV • TBV • Buffett Adjusted • Net-Net)</h3>

            {/* Auto/Manual controls */}
            <div className="assump-card">
                <div className="assump-header">
                    <div className="assump-title">Assumptions (Discounts applied to asset buckets)</div>
                    <div className="assump-ctrls">
                        <label className="toggle">
                            <input
                                type="checkbox"
                                checked={autoMode}
                                onChange={(e) => setAutoMode(e.target.checked)}
                            />
                            <span>{autoMode ? "Auto: Buffett Rules" : "Manual: Free Adjust"}</span>
                        </label>

                        <select
                            value={sector}
                            onChange={(e) => setSector(e.target.value)}
                            title="Sector/Industry preset"
                        >
                            {Object.keys(SECTOR_BASE).map((k) => (
                                <option key={k} value={k}>{k}</option>
                            ))}
                        </select>

                        <button className="btn-reset" onClick={reset} disabled={autoMode}>
                            Reset to Buffett defaults
                        </button>
                    </div>
                </div>

                {/* Why this setting */}
                {autoMode && whyNotes?.length > 0 && (
                    <div className="why">
                        <div className="why-title">Why these discounts?</div>
                        <ul>{whyNotes.map((n, i) => <li key={i}>{n}</li>)}</ul>
                        <div className="why-mini">
                            Inputs — Debtor Days: {fmt(debtorDays, 0)} • Inventory Days: {fmt(inventoryDays, 0)} • CFO: ₹{fmt(cfo, 0)} • PAT: ₹{fmt(pat, 0)}
                        </div>
                    </div>
                )}

                <div className="assump-grid" style={dis}>
                    {[
                        { k: "cash", label: "Cash", hint: "Usually 0% discount" },
                        { k: "receivables", label: "Accounts Receivable", hint: "Based on Debtor Days" },
                        { k: "inventory", label: "Inventory", hint: "Based on Inventory Days" },
                        { k: "ppe", label: "PPE / Fixed Assets", hint: "Sector risk" },
                        { k: "residual", label: "Residual (everything else)", hint: "Other low-realization assets" },
                    ].map(({ k, label, hint }) => (
                        <div key={k} className="assump-row">
                            <div className="assump-label">
                                <div className="lbl">{label}</div>
                                <div className="hint">{hint}</div>
                            </div>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                step={1}
                                value={disc[k]}
                                onChange={(e) => handlePct(k, e.target.value)}
                            />
                            <div className="assump-pct">
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={disc[k]}
                                    onChange={(e) => handlePct(k, e.target.value)}
                                />
                                <span>%</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Cards */}
            <div className="val-grid">
                <div className="val-card">
                    <div className="val-title">Book Value (Equity)</div>
                    <div className="val-main">₹ {fmt(equityBook, 0)}</div>
                    <div className="val-sub">BVPS: ₹ {fmt(bvps)}</div>
                    <div className="val-row"><span>P/B</span><span>{fmt(pb, 2)}</span></div>
                    <div className={chip(mos(bvps) != null && mos(bvps) > 0.2)}>
                        MoS vs BVPS: {mos(bvps) == null ? "–" : (mos(bvps) * 100).toFixed(0) + "%"}
                    </div>
                </div>

                <div className="val-card">
                    <div className="val-title">Tangible Book (ex-intangibles)</div>
                    <div className="val-main">₹ {fmt(tangibleBook, 0)}</div>
                    <div className="val-sub">TBVPS: ₹ {fmt(tbvps)}</div>
                    <div className="val-row"><span>P/TBV</span><span>{fmt(ptbv, 2)}</span></div>
                    <div className={chip(mos(tbvps) != null && mos(tbvps) > 0.2)}>
                        MoS vs TBVPS: {mos(tbvps) == null ? "–" : (mos(tbvps) * 100).toFixed(0) + "%"}
                    </div>
                </div>

                <div className="val-card">
                    <div className="val-title">Buffett-Style Adjusted Book</div>
                    <div className="val-main">₹ {fmt(adjusted, 0)}</div>
                    <div className="val-sub">AdjBVPS: ₹ {fmt(adjps)}</div>
                    <div className="val-row"><span>P/AdjBV</span><span>{fmt(padj, 2)}</span></div>
                    <div className={chip(mos(adjps) != null && mos(adjps) > 0.2)}>
                        MoS vs AdjBVPS: {mos(adjps) == null ? "–" : (mos(adjps) * 100).toFixed(0) + "%"}
                    </div>
                    <div className="val-note">
                        Discounts — Cash: {disc.cash}% • AR: {disc.receivables}% • Inv: {disc.inventory}% • PPE: {disc.ppe}% • Residual: {disc.residual}%
                    </div>
                </div>

                <div className="val-card">
                    <div className="val-title">Net-Net (NCAV) & ⅔ Rule</div>
                    <div className="val-main">NCAV: ₹ {fmt(ncav, 0)}</div>
                    <div className="val-sub">NCAV/share: ₹ {fmt(ncavps)}</div>
                    <div className="val-row"><span>⅔ of NCAV/share</span><span>₹ {fmt(t2nps)}</span></div>
                    <div className="val-row"><span>P/NCAV</span><span>{fmt(pncav, 2)}</span></div>
                    <div className={chip(price != null && t2nps != null && price < t2nps)}>
                        {price != null && t2nps != null && price < t2nps ? "Below ⅔-NCAV (deep value)" : "⅔-NCAV not met"}
                    </div>
                </div>
            </div>

            {/* styles */}
            <style jsx>{`
        .assump-card {
          border: 1px solid var(--border-color, #e6e6e6);
          border-radius: 12px;
          padding: 12px;
          margin-top: 8px;
          background: var(--card-bg, #fff);
        }
        .assump-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; flex-wrap:wrap; gap:8px; }
        .assump-title { font-weight:600; }
        .assump-ctrls { display:flex; gap:8px; align-items:center; }
        .toggle { display:flex; gap:6px; align-items:center; font-weight:600; }
        .btn-reset { border:1px solid #ddd; background:#fafafa; padding:6px 10px; border-radius:8px; cursor:pointer; }
        .btn-reset:disabled { opacity:.5; cursor:not-allowed; }
        .btn-reset:hover { background:#f0f0f0; }
        .assump-grid { display:grid; gap:10px; }
        .assump-row { display:grid; grid-template-columns: 240px 1fr 84px; align-items:center; gap:12px; }
        .assump-label .lbl { font-weight:600; }
        .assump-label .hint { font-size:.8rem; color:#777; }
        .assump-pct { display:flex; align-items:center; gap:6px; }
        .assump-pct input { width:56px; padding:6px; }
        input[type="range"] { width:100%; }

        .why { margin:8px 0 4px; background:var(--why-bg,#fafafa); border:1px dashed #ddd; padding:8px; border-radius:8px; }
        .why-title { font-weight:700; margin-bottom:4px; }
        .why-mini { font-size:.8rem; color:#666; margin-top:4px; }

        .val-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); margin-top: 12px; }
        .val-card { border: 1px solid var(--border-color, #e6e6e6); border-radius: 12px; padding: 14px; background: var(--card-bg, #fff); }
        .val-title { font-weight: 600; margin-bottom: 6px; }
        .val-main { font-size: 1.25rem; font-weight: 700; }
        .val-sub { color: #666; margin: 4px 0 10px; }
        .val-row { display:flex; justify-content:space-between; margin: 4px 0; }
        .val-note { font-size: 0.82rem; color: #777; margin-top: 6px; }
        .chip { display:inline-block; margin-top:8px; padding:3px 8px; border-radius:999px; font-size:.78rem; background:#eee; color:#333; }
        .chip.green { background: rgba(40,167,69,.15); color: #155724; }
        .chip.red { background: rgba(220,53,69,.15); color: #842029; }
        :root[data-theme="dark"] .assump-card,
        :root[data-theme="dark"] .val-card { background: #111; border-color: #2a2a2a; }
        :root[data-theme="dark"] .val-sub { color: #aaa; }
      `}</style>
        </div>
    );
}
